import { useMemo, useRef, useState } from "react";
import { useModalFocus } from "../../shared/useModalFocus";
import { PhaseBadge } from "./PhaseBadge";
import { Segmented } from "../../shared/Segmented";
import ReactDOM from "react-dom";
import { C } from "../../shared/theme";
import { goalColorTokens } from "../../shared/goalColors";
import { dueWords } from "../../shared/deadlines";
import { GOAL_VIEWS } from "../tasks/goalOrder";
import { PAGE_WIDTH } from "../../shared/layout";
import {
  PHASE_LABELS, PHASE_WHEN, suggestions, matchTasks, goalsWithTasks, tasksWithoutGoal, addableTasks,
} from "./addToPhase";

// Only overdue gets a colour; green is kept for what you can tap.
const WHY_COLOR = { overdue: () => C.red, soon: () => C.textMid, goal: () => C.textMid };
// Functions, not constants: C swaps between the light and dark palettes after this file loads.
const sectionLabel = () => ({ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textMid });
// "Due in 2 days · Oct 20", red once it is overdue.
const dueLine = (task) => {
  const due = dueWords(task.deadline);
  return due ? { text: due.text, color: due.overdue ? C.red : C.textMid } : null;
};
const card = () => ({ background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: 14, overflow: "hidden" });

// One task you can add: its words, a line under them (a reason, its goal or its deadline), "In Morning"
// when it is planned for another part of the day, and Add or Move.
function PickRow({ task, phaseId, detail, detailColor, dot, first, onPick }) {
  const elsewhere = task.todayPhase && task.todayPhase !== phaseId;
  const verb = elsewhere ? "Move" : "Add";
  return (
    <button onClick={() => onPick(task)} aria-label={`${verb} "${task.text}" to ${PHASE_LABELS[phaseId]}`}
      style={{ width: "100%", minHeight: 52, display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 12px", background: "none", border: "none", borderTop: first ? "none" : `1px solid ${C.border}`, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
      {dot && <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: "50%", background: dot, marginTop: 6, flexShrink: 0 }} />}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 15, lineHeight: 1.35, color: C.text }}>{task.text}</span>
        {detail && <span style={{ display: "block", fontSize: 13, fontWeight: 600, marginTop: 2, color: detailColor || C.textMid }}>{detail}</span>}
        {elsewhere && <PhaseBadge phaseId={task.todayPhase} style={{ marginTop: 5 }} />}
      </span>
      <span aria-hidden="true" style={{ flexShrink: 0, fontSize: 14, fontWeight: 700, color: C.textGreen, paddingTop: 1 }}>{verb}</span>
    </button>
  );
}

// A goal (or "Tasks without a goal"): tap to show its tasks. Closed until tapped. The goal's colour is on
// the border and the dot; the words stay in the text colours so they are readable in both themes.
function GoalGroup({ title, rank, view, color, tasks, phaseId, onPick }) {
  const [open, setOpen] = useState(false);
  const tint = color || C.borderCard;
  const count = `${tasks.length} ${tasks.length === 1 ? "task" : "tasks"}`;
  const label = rank != null ? `${title}, number ${rank} by ${view}, ${count}` : `${title}, ${count}`;
  return (
    <div>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} aria-label={label}
        style={{ width: "100%", minHeight: 48, display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: C.surface, border: `1.5px solid ${tint}`, borderRadius: open ? "12px 12px 0 0" : 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
        {rank != null && <span style={{ fontSize: 13, fontWeight: 800, minWidth: 24, color: C.textMid }}>#{rank}</span>}
        {color && <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: "50%", background: color, flexShrink: 0 }} />}
        <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, lineHeight: 1.3, color: C.text }}>{title}</span>
        <span style={{ fontSize: 13, fontWeight: 600, flexShrink: 0, color: C.textMid }}>{count} <span aria-hidden="true">{open ? "▲" : "▼"}</span></span>
      </button>
      {open && (
        <div style={{ ...card(), border: `1.5px solid ${tint}`, borderTop: "none", borderRadius: "0 0 12px 12px" }}>
          {tasks.map((t, i) => {
            const due = dueLine(t);
            return <PickRow key={t.id} task={t} phaseId={phaseId} first={i === 0} onPick={onPick} detail={due?.text} detailColor={due?.color} />;
          })}
        </div>
      )}
    </div>
  );
}

// Today's "+ Add task": search or start a new task, a few suggestions with their reasons, then your goals
// in importance or urgency order. onPick(task) adds it to this part of the day; onNew(text) starts a new
// task here; onHelpMeChoose() opens the guided ranking. Firestore stays with the caller.
export function AddToPhaseSheet({ phaseId, tasks, goals, dueSoonWindow, onPick, onNew, onHelpMeChoose, onClose }) {
  const [query, setQuery] = useState("");
  const [view, setView] = useState(GOAL_VIEWS.IMPORTANCE);
  const [searchFocused, setSearchFocused] = useState(false);
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const phase = PHASE_LABELS[phaseId];
  const goalById = useMemo(() => new Map((goals || []).map(g => [g.id, g])), [goals]);

  const searching = query.trim().length > 0;
  const suggested = useMemo(() => suggestions(tasks, goals, phaseId, dueSoonWindow), [tasks, goals, phaseId, dueSoonWindow]);
  const matches = useMemo(() => matchTasks(tasks, phaseId, query), [tasks, phaseId, query]);
  const goalGroups = useMemo(() => goalsWithTasks(tasks, goals, phaseId, view), [tasks, goals, phaseId, view]);
  const loose = useMemo(() => tasksWithoutGoal(tasks, goals, phaseId), [tasks, goals, phaseId]);
  const nothingToAdd = addableTasks(tasks, phaseId).length === 0;

  const hiddenGoals = (goals || []).filter(g => g.status === "active").length - goalGroups.length;

  // Focus moves to the heading (not the search box, which would raise a phone's keyboard).
  useModalFocus({ containerRef: dialogRef, startRef: headingRef, onClose, fallbackSelector: `[data-add-task="${phaseId}"]` });

  const dotFor = (task) => { const g = goalById.get(task.parentGoalId); return g ? goalColorTokens(g.color).main : C.textMid; };
  const goalName = (task) => goalById.get(task.parentGoalId)?.title || null;

  return ReactDOM.createPortal(
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 500 }} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="add-to-phase-title"
        style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501, margin: "0 auto", maxWidth: PAGE_WIDTH, height: "85dvh", background: C.surface, borderRadius: "22px 22px 0 0", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 -4px 30px rgba(0,0,0,0.15)", animation: "slideUp 0.25s cubic-bezier(0.32,0.72,0,1)" }}>
        <div aria-hidden="true" style={{ width: 40, height: 4, borderRadius: 4, background: C.border, margin: "10px auto 0", flexShrink: 0 }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px 10px", flexShrink: 0 }}>
          <h2 id="add-to-phase-title" ref={headingRef} tabIndex={-1} style={{ outline: "none", margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>Add to {phase}</h2>
          <button onClick={onClose} aria-label="Close"
            style={{ width: 44, height: 44, borderRadius: "50%", border: "none", background: C.surface2, color: C.textMid, fontSize: 16, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 8, padding: "0 16px 12px", flexShrink: 0 }}>
          <input value={query} onChange={e => setQuery(e.target.value)} aria-label="Search your tasks" placeholder="Search your tasks"
            onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
            style={{ flex: 1, minWidth: 0, height: 44, background: C.surface2, border: `1px solid ${searchFocused ? C.accent : C.border}`, boxShadow: searchFocused ? `0 0 0 2px ${C.accentMid}` : "none", borderRadius: C.rsm, padding: "0 12px", fontSize: 16, color: C.text, fontFamily: "inherit", outline: "none" }} />
          <button onClick={() => onNew(query.trim())}
            style={{ height: 44, padding: "0 14px", borderRadius: C.rsm, border: "none", background: C.accent, color: C.onAccent, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
            + New
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "0 16px calc(32px + env(safe-area-inset-bottom, 0px))", display: "grid", gap: 18, alignContent: "start" }}>
          {searching ? (
            <>
              {matches.length === 0 && (
                <p style={{ margin: "4px 0 0", fontSize: 15, lineHeight: 1.5, color: C.textMid }}>No tasks match "{query.trim()}".</p>
              )}
              {matches.length > 0 && (
                <section>
                  <h3 style={{ ...sectionLabel(), margin: "0 0 6px" }}>{matches.length === 1 ? "1 task matches" : `${matches.length} tasks match`} "{query.trim()}"</h3>
                  <div style={card()}>
                    {matches.map((t, i) => (
                      <PickRow key={t.id} task={t} phaseId={phaseId} first={i === 0} onPick={onPick} dot={dotFor(t)}
                        detail={[goalName(t) || "No goal", dueLine(t)?.text].filter(Boolean).join(" · ")} detailColor={dueLine(t)?.color} />
                    ))}
                  </div>
                </section>
              )}
              <button onClick={() => onNew(query.trim())}
                style={{ ...card(), minHeight: 48, padding: "12px 14px", cursor: "pointer", fontFamily: "inherit", textAlign: "left", fontSize: 15, fontWeight: 700, color: C.textGreen }}>
                + Make "{query.trim()}" a new task for {phase}
              </button>
            </>
          ) : nothingToAdd ? (
            <p style={{ margin: "4px 0 0", fontSize: 15, lineHeight: 1.5, color: C.textMid }}>
              {(tasks || []).length === 0
                ? `You don't have any tasks yet. Tap "+ New" to add one to your ${phase}.`
                : (tasks || []).every(t => t.done)
                  ? `You've finished all your tasks. Tap "+ New" to add another.`
                  : `All your tasks are already in your ${phase}. Tap "+ New" to add another.`}
            </p>
          ) : (
            <>
              {goalGroups.length > 0 && (
                <button onClick={onHelpMeChoose}
                  style={{ width: "100%", minHeight: 56, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.accentLight, border: `1px solid ${C.accentMid}`, borderRadius: C.rsm, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: C.textGreen }}>Not sure what to do?</span>
                    <span style={{ display: "block", fontSize: 14, color: C.textMid, marginTop: 2 }}>Help me choose from my goals</span>
                  </span>
                  <span aria-hidden="true" style={{ fontSize: 20, color: C.textGreen }}>›</span>
                </button>
              )}
              {suggested.length > 0 && (
                <section>
                  <h3 style={{ ...sectionLabel(), margin: "0 0 6px" }}>Worth a look {PHASE_WHEN[phaseId]}</h3>
                  <div style={card()}>
                    {suggested.map(({ task, why, tone }, i) => (
                      <PickRow key={task.id} task={task} phaseId={phaseId} first={i === 0} onPick={onPick} dot={dotFor(task)}
                        detail={why} detailColor={WHY_COLOR[tone]()} />
                    ))}
                  </div>
                </section>
              )}
              {goalGroups.length > 0 && (
                <section>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                    <h3 style={{ ...sectionLabel(), margin: 0 }}>Your goals</h3>
                    <Segmented label="Sort goals by" options={[[GOAL_VIEWS.IMPORTANCE, "Importance"], [GOAL_VIEWS.URGENCY, "Urgency"]]} value={view} onChange={setView} />
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {goalGroups.map(({ goal, rank, tasks: gt }) => (
                      <GoalGroup key={`${view}-${goal.id}`} title={goal.title} rank={rank} view={view === GOAL_VIEWS.IMPORTANCE ? "importance" : "urgency"} color={goalColorTokens(goal.color).main} tasks={gt} phaseId={phaseId} onPick={onPick} />
                    ))}
                    {loose.length > 0 && <GoalGroup title="Tasks without a goal" tasks={loose} phaseId={phaseId} onPick={onPick} />}
                  </div>
                  {hiddenGoals > 0 && (
                    <p style={{ margin: "8px 2px 0", fontSize: 14, lineHeight: 1.45, color: C.textMid }}>
                      {hiddenGoals === 1 ? "1 goal isn't shown because it has no tasks to add." : `${hiddenGoals} goals aren't shown because they have no tasks to add.`}
                    </p>
                  )}
                </section>
              )}
              {goalGroups.length === 0 && loose.length > 0 && (
                <section>
                  <h3 style={{ ...sectionLabel(), margin: "0 0 6px" }}>Your tasks</h3>
                  <div style={card()}>
                    {loose.map((t, i) => { const due = dueLine(t); return <PickRow key={t.id} task={t} phaseId={phaseId} first={i === 0} onPick={onPick} detail={due?.text} detailColor={due?.color} />; })}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
